import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { subcontractorService } from "subcontractors/services";
import { ISimpleSubcontractor } from "subcontractors/models";

export const subcontractorKeys = {
  all: ["subcontractors"] as const,
  list: () => [...subcontractorKeys.all, "list"] as const,
  members: (subcontractorId?: string) =>
    [...subcontractorKeys.all, "members", subcontractorId ?? "unknown"] as const,
  memberOptions: (subcontractorId?: string) =>
    [...subcontractorKeys.all, "member-options", subcontractorId ?? "unknown"] as const,
};

/**
 * Fetches all subcontractors.
 */
export function useSubcontractors(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: subcontractorKeys.list(),
    queryFn: () => subcontractorService.getSimpleSubcontractors(),
    staleTime: 5 * 60_000,
    ...options,
  });
}

/**
 * Fetches members of a specific subcontractor.
 */
export function useSubcontractorMembers(
  subcontractorId?: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: subcontractorKeys.members(subcontractorId),
    queryFn: () => subcontractorService.getSubcontractorMembers(subcontractorId as string),
    enabled: !!subcontractorId && (options?.enabled ?? true),
    staleTime: 5 * 60_000,
    ...options,
  });
}

/**
 * Fetches subcontractor member select options.
 */
export function useSubcontractorMemberOptions(
  subcontractorId?: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: subcontractorKeys.memberOptions(subcontractorId),
    queryFn: () => subcontractorService.getSubcontractorMemberSelectOptions(subcontractorId),
    staleTime: 5 * 60_000,
    ...options,
  });
}

/**
 * Mutation to create or update a subcontractor.
 * Invalidates subcontractors queries on success.
 */
export function useUpsertSubcontractorMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (subcontractor: ISimpleSubcontractor) =>
      subcontractor.id
        ? subcontractorService.updateSubcontractor(subcontractor)
        : subcontractorService.createSubcontractor(subcontractor),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subcontractorKeys.all });
    },
  });
}

/**
 * Mutation to delete a subcontractor.
 * Invalidates subcontractors queries on success.
 */
export function useDeleteSubcontractorMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => subcontractorService.deleteSubcontractor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subcontractorKeys.all });
    },
  });
}
